async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // El WAF del servidor (ModSecurity) rebutja amb 403 els POST/PATCH sense cos.
  // Per garantir que hi passin, sempre enviem un cos JSON (buit per defecte)
  // amb el seu Content-Type en aquests mètodes.
  const needsBody = method === "POST" || method === "PATCH";
  const payload = body !== undefined ? body : needsBody ? {} : undefined;
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: payload !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: payload !== undefined ? JSON.stringify(payload) : undefined,
  });
  if (!res.ok) {
    let msg = res.statusText;
    let data: any = {};
    try {
      data = await res.json();
      msg = data.error ?? msg;
      console.log("✅ API Error response:", data);
    } catch (e) {
      console.log("❌ Failed to parse JSON:", e);
    }
    if (res.status === 401) msg = data.error ?? "no autenticado";
    console.log("Setting code:", data.code, "recipes:", data.recipes);
    const err = new ApiError(msg, res.status);
    err.code = data.code;
    err.recipes = data.recipes;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  code?: string;
  recipes?: Array<{ id: number; name: string }>;

  constructor(message: string, public status: number) {
    super(message);
  }
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

// ----- Tipos compartidos con el backend -----
export const MEAL_TYPES = ["breakfast", "lunch", "snack", "subrecipe"] as const;
export type MealType = (typeof MEAL_TYPES)[number];

export function isSubrecipe(mealType: MealType): boolean {
  return mealType === "subrecipe";
}
// Una recepta-embolcall d'un sol ingredient (creada automàticament des d'un
// ingredient) porta ingredientId != null. S'edita des de la secció Ingredients.
export function isIngredientRecipe(r: { ingredientId?: number | null }): boolean {
  return r.ingredientId != null;
}
export type MealSlot =
  | "breakfast1"
  | "breakfast2"
  | "lunch"
  | "snack"
  | "dinner";

export type Role = "admin" | "member";

export interface SlotException {
  day: number;
  mealSlot: MealSlot;
}

export interface AppUser {
  id: number;
  email: string;
  name: string;
  role: Role;
  avatarColor: string;
  avatarEmoji: string;
  printLabel: string;
  preferences?: unknown;
  intolerances?: unknown;
  position: number;
  slotDefaults: MealSlot[];
  slotExceptions: SlotException[];
  birthYear?: number | null;
  sex?: Sex | null;
  heightCm?: number | null;
  weightKg?: number | null;
  activityLevel?: ActivityLevel | null;
}

export type Sex = "male" | "female";
export type ActivityLevel = "sedentary" | "light" | "moderate" | "active";

/** Perfil nutricional d'un usuari (per a la revisió nutricional de la setmana). */
export interface NutritionProfile {
  birthYear: number | null;
  sex: Sex | null;
  heightCm: number | null;
  weightKg: number | null;
  activityLevel: ActivityLevel | null;
}

export function nutritionProfileOf(u: AppUser): NutritionProfile {
  return {
    birthYear: u.birthYear ?? null,
    sex: u.sex ?? null,
    heightCm: u.heightCm ?? null,
    weightKg: u.weightKg ?? null,
    activityLevel: u.activityLevel ?? null,
  };
}

export type NutritionStatus = "ok" | "warn" | "bad" | "na";

/** Revisió nutricional d'una persona per a una setmana (GET /weeks/:date/review). */
export interface PersonNutrition {
  userId: number;
  name: string;
  avatarEmoji: string;
  avatarColor: string;
  age: number | null;
  missingProfile: string[];
  targetKcal: number | null;
  targetNotes: string[];
  expectedMeals: number;
  plannedMeals: number;
  completeDays: number;
  cardsWithoutData: number;
  avgKcal: number | null;
  macros: { protein: number; carbs: number; fat: number } | null;
  ranges: { protein: [number, number]; carbs: [number, number]; fat: [number, number] };
  ultraPct: number | null;
  status: { energy: NutritionStatus; protein: NutritionStatus; carbs: NutritionStatus; fat: NutritionStatus; ultra: NutritionStatus };
}

export interface RecipeListItem {
  id: number;
  name: string;
  mealType: MealType;
  baseServings: number;
  prepTimeMin: number | null;
  tags: string[];
  favorite: boolean;
  imageUrl: string | null;
  ingredientId?: number | null;
}

export type NovaGroup = 1 | 2 | 3 | 4;
export type AdditiveRiskLevel = "zero" | "low" | "medium" | "high";
export type NutriSource = "USDA" | "OFF" | "BEDCA" | "IA" | "MANUAL";
/** Estat de l'aliment al qual correspon el perfil per 100 g; null = sense especificar (cru) */
export type IngredientState = "raw" | "cooked" | "fried" | "dried" | "canned" | "frozen";

/**
 * IMPORTANT: tots els valors nutricionals són sempre per 100 g de part comestible.
 *
 * calories: kcal/100g
 * fat, saturatedFat, polyunsaturatedFat, monounsaturatedFat, transFat,
 * carbs, fiber, sugar, protein: g/100g
 * cholesterol, sodium, potassium, calcium, iron, magnesium, zinc, vitaminC: mg/100g
 * vitaminA (µg RAE), vitaminD, vitaminB12: µg/100g
 */
export interface NutritionData {
  calories?: number | null;
  fat?: number | null;
  saturatedFat?: number | null;
  polyunsaturatedFat?: number | null;
  monounsaturatedFat?: number | null;
  transFat?: number | null;
  cholesterol?: number | null;
  sodium?: number | null;
  potassium?: number | null;
  carbs?: number | null;
  fiber?: number | null;
  sugar?: number | null;
  protein?: number | null;
  vitaminA?: number | null;
  vitaminC?: number | null;
  vitaminD?: number | null;
  vitaminB12?: number | null;
  magnesium?: number | null;
  zinc?: number | null;
  calcium?: number | null;
  iron?: number | null;
}

export interface IngredientDetail extends NutritionData {
  id: number;
  name: string;
  nameEn?: string | null;
  defaultUnit?: string | null;
  gramsPerUnit?: number | null;
  imageUrl?: string | null;
  fdcId?: number | null;
  /** Codi de barres genèric (EAN/UPC/GTIN); serveix per OFF i USDA Branded. */
  barcode?: string | null;
  nutriSource?: NutriSource | null;
  state?: IngredientState | null;
  novaGroup?: NovaGroup | null;
  additiveRiskLevel?: AdditiveRiskLevel | null;
  /** CSV en català: "gluten, lactosa, ous" */
  allergens?: string | null;
  shopZone?: string | null;
  isPantry?: boolean;
  /** Es pot afegir sol a la graella del menú (fruita, snacks, embotits…). */
  canBeMeal?: boolean;
}

export interface ExternalIngredient {
  source: "USDA" | "OFF";
  externalId: string;
  name: string;
  brand?: string;
  displayName?: string;
  linkableId?: string;
  nutrition: Partial<NutritionData>;
  novaGroup?: NovaGroup | null;
  additiveRiskLevel?: AdditiveRiskLevel | null;
  allergens?: string | null;
  imageUrl?: string | null;
}

export type PrepAheadKind = "soak" | "thaw" | "fridge" | "other";

export const PREP_AHEAD_KINDS: { value: PrepAheadKind; label: string; verb: string; emoji: string }[] = [
  { value: "soak", label: "Remull", verb: "Posar en remull", emoji: "💧" },
  { value: "thaw", label: "Descongelar", verb: "Descongelar", emoji: "🧊" },
  { value: "fridge", label: "Nevera (overnight)", verb: "Deixar a la nevera", emoji: "🌙" },
  { value: "other", label: "Altres", verb: "Preparar", emoji: "⏲️" },
];

export interface ScaledIngredient {
  id: number;
  ingredientId: number;
  name: string;
  quantity: number;
  unit: string;
  note: string | null;
  prepAhead?: PrepAheadKind | null;
  prepLeadDays?: number;
  prepNote?: string | null;
  gramsPerUnit?: number | null;
  nutrition?: NutritionData | null;
}

export interface ScaledRecipeBase {
  id: number;
  name: string;
  mealType: MealType;
  baseServings: number;
  effectiveServings: number;
  factor: number;
  prepTimeMin: number | null;
  notes: string | null;
  tags: string[];
  ingredients: ScaledIngredient[];
  steps: string[];
  imageUrl: string | null;
}

export interface ScaledRecipe extends ScaledRecipeBase {
  subrecipes: ScaledRecipeBase[];
  favorite: boolean;
}

export interface NutritionMacros {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  ultraCalories?: number;
}

export interface SlotCard {
  id: number;
  slotId: number;
  recipeId: number;
  position: number;
  overrideServings: number | null;
  recipe: {
    id: number;
    name: string;
    mealType: MealType;
    baseServings: number;
    prepTimeMin: number | null;
    imageUrl: string | null;
  };
  users: { userId: number }[];
  nutritionPerServing?: NutritionMacros | null;
}

export interface WeekSlotDto {
  id: number;
  weekId: number;
  day: number;
  mealSlot: MealSlot;
  cards: SlotCard[];
}

export interface WeekDto {
  id: number;
  startDate: string;
  name: string | null;
  isTemplate: boolean;
  slots: WeekSlotDto[];
}

export interface SavedWeekItem {
  id: number;
  name: string;
  startDate: string;
}

export interface ShoppingItem {
  id: number;
  name: string;
  quantity: number | null;
  unit: string | null;
  ingredientId: number | null;
  checked: boolean;
  position: number;
  createdAt: string;
  shopZone?: string | null;
  /** L'ingredient vinculat té codi de barres (envasat) en lloc de ser a granel. */
  packaged?: boolean;
}

export interface ShoppingHistoryEntry {
  id: number;
  items: ShoppingItem[];
  itemCount: number;
  savedAt: string;
}

export interface ShoppingHistoryResponse {
  entries: ShoppingHistoryEntry[];
  total: number;
}

export interface WeekShopItem {
  name: string;
  ingredientId: number | null;
  quantity: number | null;
  unit: string | null;
  shopZone: string | null;
}

export interface WeekShoppingListResponse {
  items: WeekShopItem[];
  mealCount: number;
}

export interface Improvement {
  id: number;
  text: string;
  authorName: string;
  authorEmoji: string;
  authorColor: string;
  position: number;
  createdAt: string;
}

export interface ImprovementHistoryEntry {
  id: number;
  text: string;
  authorName: string;
  authorEmoji: string;
  authorColor: string;
  completedAt: string;
}

export interface ImprovementHistoryResponse {
  entries: ImprovementHistoryEntry[];
  total: number;
}

// ----- Preparació prèvia (notificacions) -----
export interface PrepTaskDto {
  id: number;
  prepAhead: PrepAheadKind;
  kindLabel: string;
  ingredient: string;
  prepNote: string | null;
  recipeId: number;
  recipeName: string;
  mealDate: string; // YYYY-MM-DD
  dueDate: string; // YYYY-MM-DD
  status: "pending" | "done";
  doneBy: { id: number; name: string; emoji: string; color: string } | null;
  doneAt: string | null;
}

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsResponse {
  unread: number;
  items: AppNotification[];
}
